import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { environment } from '@environments/environment';
import { URL_PARAMS } from '@global/constants';
import { Flow, RoutePaths } from '@global/routePaths';
import { Store } from '@ngrx/store';
import { LoadingService } from '@services/loading/loading.service';
import { QueryParamsStore } from '@services/queryparams-store/queryparams-store.service';
import { AppState } from '@store/app.state';

import cloneDeep from 'clone-deep';
import { Observable, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PageGuard implements CanActivate {
  readonly TK = 'tk';
  readonly RTK = 'rtk';
  readonly PT_ID = 'ptId';
  readonly TYPE_VEHICLE = 'r';
  readonly PRIVACY_CONSENT = 'ppc';
  readonly PLATE_NUMBER = 'pn';
  readonly BIRTH_DATE = 'bd';
  readonly FLOW = 'fl'; //Just in case of IVASS
  readonly ID = 'id'; //Just in case of IVASS
  readonly TOKEN = 'token'; // Just in case of RFS
  readonly P_ID = 'pId';

  // readonly UTM_SOURCE = 'utm_source';
  // readonly UTM_MEDIUM = 'utm_medium';
  // readonly UTM_CAMPAIGN = 'utm_campaign';
  // readonly UTM_TERM = 'utm_term';
  // readonly UTM_CONTENT = 'utm_content';
  // readonly UTM_ID = 'utm_id';
  readonly PARTNER_ID = 'partnerid';
  readonly GCLID = 'gclid';
  readonly CLICK_ID = 'click_id';
  readonly DCLID = 'dclid';
  readonly GAD_SOURCE = 'gad_source';
  readonly FBCLID = 'fbclid';
  readonly MSCLKID = 'msclkid';
  readonly GTM_DEBUG = 'gtm_debug';
  readonly TWCLID = 'twclid';
  readonly TTCLID = 'ttclid';

  readonly MOCKED = 'mocked';

  private continueToErrorPage = false;
  private continueToMaintenancePage = false;

  userId: string;
  projectId: string;
  policyId: string;
  isBackOfficeUser: boolean;
  version: string;

  vehicleType: string;
  plateNumber: string;
  birthdate: string;
  privacyConsent: string;
  skipPartnership: boolean = true;
  gtmDebug: string;

  flow: string;
  id: string;

  campaignCode: string;
  promotionCode: string;
  referralCode: string;

  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
  utmContent: string;
  utmId: string;
  partnerId: string;
  gclid: string;
  clickId: string;
  dclid: string;
  gadSource: string;
  fbclid: string;
  msclkid: string;
  twclid: string;
  ttclid: string;
  sId: string;
  qs: string;
  ak: string;
  entrypoint: string;
  empfehlung: string;
  ch: string;
  einsprungsquelle: string;
  nr: string;
  verVU: string;
  verVM: string;
  parVM: string;
  vorVM: string;
  antVM: string;
  vehicle_kind: string;

  private cookies: Map<string, string>;

  constructor(
    private readonly router: Router,
    private readonly store: Store<AppState>,
    private readonly loadingService: LoadingService,
    private readonly queryParamsStore: QueryParamsStore
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    this.continueToErrorPage = this.router.getCurrentNavigation()?.extras?.state?.continueToErrorPage ?? false;

    this.continueToMaintenancePage =
      this.router.getCurrentNavigation()?.extras?.state?.continueToMaintenancePage ?? false;

    if (environment.environmentName === 'SND' && state.url === '/maintenance-page') {
      this.continueToMaintenancePage = true;
    }

    this.campaignCode = route.queryParams?.campaignCode;
    this.promotionCode = route.queryParams?.promotionCode;
    this.referralCode = route.queryParams?.referralCode;

    this.utmSource = route.queryParams?.[URL_PARAMS.utm_source];
    this.utmMedium = route.queryParams?.[URL_PARAMS.utm_medium];
    this.utmCampaign = route.queryParams?.[URL_PARAMS.utm_campaign];
    this.utmTerm = route.queryParams?.[URL_PARAMS.utm_term];
    this.utmContent = route.queryParams?.[URL_PARAMS.utm_content];
    this.utmId = route.queryParams?.[URL_PARAMS.utm_id];
    this.partnerId = route.queryParams?.[URL_PARAMS.partner_id];
    this.gclid = route.queryParams?.[URL_PARAMS.gclid];
    this.clickId = route.queryParams?.[URL_PARAMS.click_id];
    this.dclid = route.queryParams?.[URL_PARAMS.dclid];
    this.gadSource = route.queryParams?.[URL_PARAMS.gad_source];
    this.fbclid = route.queryParams?.[URL_PARAMS.fbclid];
    this.msclkid = route.queryParams?.[URL_PARAMS.msclkid];
    this.gtmDebug = route.queryParams?.[URL_PARAMS.gtm_debug];
    this.twclid = route.queryParams?.[URL_PARAMS.twclid];
    this.ttclid = route.queryParams?.[URL_PARAMS.ttclid];
    this.sId = route.queryParams?.[URL_PARAMS.s_id];
    this.qs = route.queryParams?.[URL_PARAMS.qs];
    this.ak = route.queryParams?.[URL_PARAMS.ak];
    this.entrypoint = route.queryParams?.[URL_PARAMS.entrypoint];
    this.empfehlung = route.queryParams?.[URL_PARAMS.empfehlung];
    this.ch = route.queryParams?.[URL_PARAMS.ch];
    this.einsprungsquelle = route.queryParams?.[URL_PARAMS.einsprungsquelle];
    this.verVM = route.queryParams?.[URL_PARAMS.verVM];
    this.verVU = route.queryParams?.[URL_PARAMS.verVU];
    this.vorVM = route.queryParams?.[URL_PARAMS.vorVM];
    this.parVM = route.queryParams?.[URL_PARAMS.parVM];
    this.antVM = route.queryParams?.[URL_PARAMS.antVM];
    this.vehicle_kind = route.queryParams?.[URL_PARAMS.vehicle_kind];

    this.gtmDebug = route.queryParams?.[this.GTM_DEBUG];

    this.queryParamsStore.capture(route.queryParams);

    return this.continueToErrorPage
      ? this.loadingService.dismissLoading().then(() => this.continueToErrorPage)
      : this.continueToMaintenancePage
        ? this.loadingService.dismissLoading().then(() => this.continueToMaintenancePage)
        : this.checkData();
  }

  private checkData(): Observable<boolean> {
    this.cookies = this.getCookies();

    return from(this.loadingService.presentLoading()).pipe(
      switchMap(() => this.redirectPage()),
      catchError(() => this.goToErrorPage())
    );
  }

  private redirectPage(): Observable<boolean> | Promise<boolean> {
    const nextRoute = RoutePaths.NEW_QUOTATION.SITUATION.VERTI_CLIENT.route;
    return this.navigateRoute(nextRoute, { flow: Flow.NEW_QUOTATION });
  }

  private navigateRoute(route: string, payload?: Record<string, unknown>): Promise<boolean> {
    return this.router
      .navigate([route], {
        queryParamsHandling: 'merge',
        queryParams: {
          campaignCode: this.campaignCode,
          promotionCode: this.promotionCode,
          referralCode: this.referralCode,
          [this.GTM_DEBUG]: this.gtmDebug,
          [URL_PARAMS.utm_source]: this.utmSource,
          [URL_PARAMS.utm_medium]: this.utmMedium,
          [URL_PARAMS.utm_campaign]: this.utmCampaign,
          [URL_PARAMS.utm_term]: this.utmTerm,
          [URL_PARAMS.utm_content]: this.utmContent,
          [URL_PARAMS.utm_id]: this.utmId,
          [URL_PARAMS.partner_id]: this.partnerId,
          [URL_PARAMS.gclid]: this.gclid,
          [URL_PARAMS.click_id]: this.clickId,
          [URL_PARAMS.dclid]: this.dclid,
          [URL_PARAMS.gad_source]: this.gadSource,
          [URL_PARAMS.fbclid]: this.fbclid,
          [URL_PARAMS.msclkid]: this.msclkid,
          [URL_PARAMS.gtm_debug]: this.gtmDebug,
          [URL_PARAMS.twclid]: this.twclid,
          [URL_PARAMS.ttclid]: this.ttclid,
          [URL_PARAMS.s_id]: this.sId,
          [URL_PARAMS.qs]: this.qs,
          [URL_PARAMS.ak]: this.ak,
          [URL_PARAMS.entrypoint]: this.entrypoint,
          [URL_PARAMS.empfehlung]: this.empfehlung,
          [URL_PARAMS.ch]: this.ch,
          [URL_PARAMS.einsprungsquelle]: this.einsprungsquelle,
          [URL_PARAMS.verVM]: this.verVM,
          [URL_PARAMS.vorVM]: this.vorVM,
          [URL_PARAMS.verVU]: this.verVU,
          [URL_PARAMS.parVM]: this.parVM,
          [URL_PARAMS.antVM]: this.antVM,
          [URL_PARAMS.test]: this.queryParamsStore.snapshot().test,
        },
        state: { ...payload, allowNavigation: true },
      })
      .finally(() => this.loadingService.dismissLoading())
      .then(() => false);
  }

  private goToErrorPage(): Promise<boolean> {
    return this.navigateRoute(RoutePaths.GENERIC_ERROR.route, { continueToErrorPage: true });
  }

  private goToMaintenancePage(): Promise<boolean> {
    return this.navigateRoute(RoutePaths.MAINTENANCE_PAGE.route, { continueToMaintenancePage: true });
  }

  protected cloneDeep<T>(value: T): T {
    // return window.structuredClone(value);
    return cloneDeep(value);
  }

  private getCookies(): Map<string, string> {
    const COOKIE_SEPARATOR = ';';
    const COOKIE_VALUE_SEPARATOR = '=';

    const rawList = document.cookie.split(COOKIE_SEPARATOR);
    const cookiePairs = rawList.map(cookiePair => cookiePair.split(new RegExp(`${COOKIE_VALUE_SEPARATOR}(.*)`, 's')));
    return cookiePairs.reduce((prev, [key, value]) => {
      prev.set(key.trim(), value);
      return prev;
    }, new Map<string, string>());
  }
}
